/**
 * English — canonical i18n source for THE GRID public surfaces.
 * All other locales mirror this exact shape (enforced by `Dict`).
 * Interpolation tokens use {curly} placeholders.
 */
const en = {
  grid: {
    controls: { group: "Grid zoom controls", zoomIn: "Zoom in", zoomOut: "Zoom out", fit: "Fit grid to viewport" },
    hud: { aria: "Grid coordinate HUD", instrument: "Instrument", xy: "X · Y", zoom: "Zoom", grid: "Grid", chain: "Chain" },
    legend: {
      gridZero: { label: "Grid Zero", desc: "Central locked nucleus" },
      vipCore: { label: "VIP Core", desc: "Prime central ring · sellable" },
      premium: { label: "Premium", desc: "Boulevards & inner ring" },
      reserve: { label: "Reserve", desc: "4 locked reserved zones" },
      standard: { label: "Standard", desc: "General territory" },
    },
    loading: { initializing: "Initializing Territory" },
    mode: { aria: "Grid interaction mode", select: "Select", selectHint: "Draw rectangle", inspect: "Inspect", inspectHint: "Click parcels", itemAria: "{label} mode — {hint}" },
    enter3d: { title: "Enter 3D", fallbackName: "territory" },
    panel: {
      parcelProfileAria: "Parcel profile", parcelProfileTitle: "Parcel Profile", parcel: "Parcel",
      territoryName: "Territory name", owner: "Owner", position: "Position", size: "Size", slots: "Slots", zone: "Zone",
      claimBlock: "Claim block", claimTx: "Claim tx", selectTerritory: "Select Territory",
      emptySelection: "Cell has no parcel. Click any owned territory to see its dossier.",
      emptyDefault: "Click a parcel on the Grid to view ownership, coordinates and on-chain identity.",
      contextAria: "Context panel", panelAria: "Panel", closePanel: "Close panel", snapshot: "Snapshot",
      titleArtwork: "Artwork", titleSelection: "Selection", titleContext: "Context",
      parcelNum: "Parcel #{id}", selectionSize: "{w} × {h} · {slots} slots", drawRectHint: "Draw a rectangle on the grid",
    },
    context: {
      emptyTitle: "No territory selected",
      emptyDesc: "Draw a rectangle on the Grid to preview slots, zone, scarcity tier, and price.",
    },
    detail: {
      fetchFailed: "Detail fetch failed: {error}",
      conflict: "Data invariant conflict at this cell: {count} parcels overlap. Selection suppressed.",
      cellOutside: "Outside grid ({x}, {y})",
      cellLoadingMap: "Loading map data…",
      cellConfirming: "Confirming cell ({x}, {y})…",
      cellEmpty: "Cell ({x}, {y}): No parcel indexed at this cell (as of block {block})",
      cellConfirmError: "Could not confirm current cell state.",
      chainMismatch: "Viewer unavailable due to chain mismatch.",
      indexerNotReady: "Indexer not ready — no snapshot yet.",
      clickHint: "Click a parcel to view details.",
      zoneClassifying: "Classifying…",
      zoneUnavailable: "Unavailable",
      zoneSaleLocked: "· sale locked",
      zoneMixed: "· zone mixed",
    },
    toolbar: {
      territory: "Territory",
      scrollToZoom: "scroll to zoom",
      menu: "Menu",
      openNav: "Open navigation",
      hideNav: "Hide navigation",
      revealAreas: "Reveal Areas",
      revealAreasAria: "Reveal territory zones",
      zonesUnavailable: "Zone map unavailable — retrying automatically",
      zonesRetry: "Retry",
      showDragArtwork: "Show & Drag Artwork",
      showDragArtworkAria: "Show and drag artwork",
      showDragArtworkTitle: "Show my artwork on the grid and drag it to another parcel",
      loading: "Loading…",
      myTerritories: "My Territories",
    },
    status: {
      live: "LIVE", cached: "CACHED", chain: "CHAIN",
      liveTitle: "Live · synchronized",
      cachedTitle: "Showing last known snapshot",
      chainTitle: "Chain mismatch — viewer data suppressed",
    },
    artworkMsg: {
      none: "You have no frozen artworks yet.",
      noPlaced: "No placed artworks to move.",
      connectWallet: "Connect your wallet to move your artworks.",
    },
    slotCount: {
      slots: "slots",
      selectedTitle: "{slots} slots selected ({w} × {h})",
    },
    bottomSheet: { aria: "Bottom sheet", dragOpen: "Drag to resize or collapse sheet", handle: "Sheet handle" },
    stats: {
      networkStatus: "Network Status", live: "Live", offline: "Offline", syncing: "Syncing", chain: "Chain",
      parcelsClaimed: "Parcels claimed", onChain: "on-chain", cellsAllocated: "Cells allocated",
      finiteWorld: "{pct}% of finite world", indexerHead: "Indexer head", finalized: "finalized #{block}",
    },
    capacity: {
      territory: "Territory", resolving: "Resolving territory…", unavailable: "Territory capacity unavailable",
      area: "Territory area", publishability: "Publishability", publishable: "Publishable",
      notPublishable: "Not publishable · Minimum {min} u²", gpCapacity: "GP capacity", propertyUnits: "Property units",
    },
    artworks: {
      title: "Artworks on this land", scanning: "Scanning…",
      empty: "No Frozen Artworks are placed on this land yet.", untitled: "Untitled artwork", view: "View →",
    },
    zoomHint: {
      copy: "Zoom in to explore The Grid. At full zoom, discover what creators have published on each parcel.",
      controlDesktop: "Scroll or trackpad to zoom",
      controlMobile: "Pinch to zoom",
      dontShow: "Don't show again",
      close: "Close",
      enteringParcels: "You're entering the parcels",
    },
  },
  nav: {
    marketplace: "Marketplace",
    feed: "Feed",
    rent: "RENT",
    overview: "Overview",
    about: "About",
    howItWorks: "How It Works",
    explore: "Explore",
    creators: "Creators & Brands",
    creatorsShort: "Creators",
    territory: "Territory",
    profile: "Profile",
    enterGrid: "Enter the Grid",
    signIn: "Sign In",
    navigate: "Navigate",
    purchaseBadge: "Purchase",
    openMenuAria: "Open navigation menu",
    closeMenuAria: "Close navigation menu",
    primaryAria: "Primary",
    howItWorksHintAria: "New here? Learn how THE GRID works",
  },
  lang: {
    ariaLabel: "Language",
    menuLabel: "Choose language",
  },
  wallet: {
    connect: "Connect",
    connecting: "Connecting…",
    myTerritories: "My Territories",
    sessionDetails: "Session details",
    disconnect: "Disconnect",
  },
  auth: {
    title: "Session",
    subtitle: "Wallet auth foundation · SIWE + EIP-1271/6492",
    walletLabel: "Wallet",
    sessionLabel: "Session",
    connectPrompt: "Connect a wallet via the top-navigation to enable Sign-In.",
    loading: "Loading…",
    signedInAs: "Signed in as {wallet}",
    expires: "expires: {date}",
    signOut: "Sign out",
    signIn: "Sign in with Ethereum",
    chain: "chain: {name} ({id})",
    status: {
      requestingNonce: "Requesting nonce…",
      requestingSignature: "Requesting signature from wallet…",
      verifying: "Verifying signature…",
      signedIn: "Signed in as {wallet} (path: {path})",
      signedOut: "Signed out",
    },
  },
  home: {
    hero: {
      eyebrow: "// ONE WORLD. FINITE POSSIBILITIES.",
      paragraph:
        "A single, finite territory of one million cells. Explore, claim, and own on-chain. Every parcel is minted from the same ledger, allocated once, forever.",
      enterGrid: "Enter the Grid",
      scrollLine1: "Scroll to",
      scrollLine2: "Explore",
      scrollAria: "Scroll to explore",
    },
    instrument: {
      title: "Instrument",
      labels: { grid: "GRID", chain: "CHAIN", maxZoom: "MAX·Z", auth: "AUTH" },
    },
    callouts: {
      gridZero: { title: "Grid Zero", body: "Nucleus of the territory" },
      lattice: { title: "Data Lattice", body: "Zone boundaries on-chain" },
      cells: { label: "Cells · Finite" },
    },
    features: [
      { title: "Finite by Design", body: "1,000,000 cells. Never more." },
      { title: "Built on Base", body: "On-chain territory · Base Sepolia today." },
      { title: "Publicly Explorable", body: "Read the map without a wallet." },
      { title: "Wallet-owned", body: "SIWE · EIP-1271 · EIP-6492 verified." },
    ],
    story: {
      eyebrow: "Our Story",
      titleLine1: "A NEW ERA",
      titleLine2: "OF",
      titleHighlight: "OWNERSHIP",
      paragraph:
        "The Grid was born from a simple idea: a digital territory that is finite by construction, and whose boundaries live inside the ledger — not the marketing page. What you own here, the chain remembers.",
    },
    roadmap: {
      phaseLabel: "Phase",
      eyebrow: "Timeline",
      phasesLive: "{live} of {total} phases live",
      title: "WHAT'S SHIPPED. WHAT'S NEXT.",
      paragraph:
        "An honest map, read straight from Base. Foundations and Ownership are live today — claim, create, publish and get discovered right now. We're in final polish and fixes now; the next major milestone is Mainnet, and it appears here only when the ledger, not the copy, makes it real.",
      status: { live: "Live", next: "In progress", later: "Planned" },
      phases: [
        {
          title: "Foundations",
          items: [
            "Canonical 1,000,000-cell territory on Base",
            "Live on-chain indexer + reconciler",
            "SIWE · EIP-1271 · EIP-6492 wallet auth",
            "Public, wallet-free exploration",
          ],
        },
        {
          title: "Ownership & Creation",
          items: [
            "Parcel claim & USDC settlement on Base",
            "Creator Canvas — links, socials, media, templates",
            "Optional 3D voxel layer",
            "Publish + honest Explore discovery",
          ],
        },
        {
          title: "Final Polish",
          items: [
            "Acceptance fixes & UX polish",
            "Full multi-language coverage",
            "Hardening & final QA before launch",
            "Parcel RENT preview page · planned release Q1 2027",
          ],
        },
        {
          title: "Mainnet",
          items: [
            "Mainnet deployment on Base",
            "Offers, secondary market & activity",
            "Certificates & creator verification",
            "Public APIs & extensions",
          ],
        },
      ],
    },
    cta: {
      eyebrow: "Enter",
      titlePrefix: "CHOOSE YOUR",
      titleHighlight: "TERRITORY",
      paragraph:
        "The map you explored is public. Ownership is not. Sign in with your wallet to place your position — the ledger will remember it.",
      exploreGrid: "Explore the Grid",
      signIn: "Sign In with Ethereum",
    },
    footer: {
      legalAria: "Legal",
      legalToggle: "Legal & Policies",
      walletAuth: "Wallet-authenticated",
      territoryData: "Territory data · read on-chain",
    },
    slidedots: {
      aria: "Section navigation",
      overview: "Overview",
      story: "Story",
      territory: "Territory",
      roadmap: "Roadmap",
      enter: "Enter",
    },
  },
  about: {
    eyebrow: "The Manifesto",
    titleLine1: "ONE FINITE",
    titleHighlight: "WORLD.",
    intro:
      "THE GRID is a single, finite digital territory of one million cells, allocated once and owned on-chain. This is what we believe, and what the product actually does — nothing more, nothing invented.",
    principles: [
      {
        title: "Finite by construction",
        body: "One territory. One million cells. Never more. Scarcity here is not a marketing choice — it is written into the contract and cannot be inflated away.",
      },
      {
        title: "Ownership lives on-chain",
        body: "A parcel is an on-chain asset on Base. What you hold is recorded by the ledger, not by us. The boundaries of your territory are verifiable, not promised.",
      },
      {
        title: "Public by default",
        body: "Anyone can read the map — no wallet, no signup. Discovery is open. Ownership is the only thing that requires you to prove who you are.",
      },
      {
        title: "A canvas, not a billboard",
        body: "Each territory is a real design surface: text, links, socials, media, shapes and an optional 3D layer, all bounded by an honest Grid-Power budget derived from your land.",
      },
      {
        title: "Your identity, verified",
        body: "Sign-In With Ethereum with EOA and smart-contract wallets (EIP-1271 / EIP-6492). We never custody your keys and never move your funds.",
      },
      {
        title: "Built to last",
        body: "Published content is a durable snapshot, separate from your working draft. Editing later never breaks what the public already sees until you republish.",
      },
    ],
  },
  how: {
    eyebrow: "The Journey",
    titleLine1: "FROM MAP",
    titleLine2: "TO OWNERSHIP.",
    intro:
      "Six real steps, each backed by a shipped surface. This is the whole loop — explore, claim, design, publish and get discovered.",
    stepLabel: "STEP {n}",
    toggle: { show: "How this works", hide: "Hide", aria: "Show a simple step-by-step explanation" },
    steps: [
      {
        title: "Explore the map",
        body: "Open the Territory and move across the finite 1000×1000 Grid. Zoom into any parcel to read who owns what — no wallet required.",
        detail: {
          intro: "Looking around is completely free and public — no wallet, no account.",
          points: [
            "Open Territory from the top menu to see the whole map: one grid of 1,000,000 small squares (cells).",
            "Drag to move, scroll to zoom. Zoom into any area to see which land is taken and what owners placed there.",
            "Nothing here asks you to sign in — reading the map is always open to everyone.",
          ],
        },
      },
      {
        title: "Claim your parcel",
        body: "Draw a rectangle on free land and claim it on Base. Ownership is minted on-chain — your position is recorded by the ledger, not by us.",
        detail: {
          intro: "\"Claiming\" means becoming the owner of a rectangle of free cells. Ownership is written on Base, a public blockchain.",
          points: [
            "You'll need a crypto wallet — a small, free browser app that proves it's you and approves actions.",
            "Today The Grid runs on Base Sepolia, a test network, so you use test USDC — not real money.",
            "On the map, drag a rectangle over free land and confirm. Your wallet signs and the parcel is minted to you.",
          ],
        },
      },
      {
        title: "Design your Canvas",
        body: "Turn your territory into a real page: titles, links, socials, contacts, media and shapes — or start from one of six premium templates. Everything fits an honest Grid-Power budget derived from your land area.",
        detail: {
          intro: "Your parcel is a blank page you fully control. Fill it, or start from a ready-made template.",
          points: [
            "Add titles, links, social buttons, contacts, images, video and simple shapes.",
            "Prefer a head start? Load one of six premium templates and tweak every detail.",
            "Each parcel has a Grid-Power budget based on its size — bigger land lets you place more. It keeps pages honest and fast.",
          ],
        },
      },
      {
        title: "Add a 3D layer (optional)",
        body: "Give your territory depth with a voxel scene that visitors can enter as they zoom in. Purely optional — a flat Canvas is a first-class result too.",
        detail: {
          intro: "On top of the flat page you can add a small 3D scene made of blocks (voxels).",
          points: [
            "Visitors step into the scene as they zoom deep into your parcel.",
            "It is completely optional — you never have to build one.",
            "A flat Canvas with no 3D is a complete, first-class result.",
          ],
        },
      },
      {
        title: "Publish",
        body: "Publishing takes a durable snapshot of your saved draft. Keep editing privately afterwards — the public sees exactly what you published until you republish.",
        detail: {
          intro: "Publishing makes a public copy of your work — while your private draft stays yours to edit.",
          points: [
            "It takes a durable snapshot of your saved draft and shows that to the world.",
            "You can keep editing privately afterwards; visitors keep seeing the published version.",
            "Nothing you change becomes public until you publish again.",
          ],
        },
      },
      {
        title: "Get discovered",
        body: "Every valid, current publication appears on Explore, newest first — no pay-to-rank, no fake trending. Real ownership, real content, honest order.",
        detail: {
          intro: "Once published, your territory is listed for everyone — automatically and fairly.",
          points: [
            "It appears on the Explore page, newest first.",
            "There is no paying to rank higher and no fake \"trending\".",
            "Real ownership, real content, honest order.",
          ],
        },
      },
    ],
    before: {
      eyebrow: "Start here",
      title: "Before you start",
      intro: "You need almost nothing to look around. You only need a wallet when you decide to own land.",
      items: [
        { title: "To explore: nothing", body: "Reading the map and opening any published page is fully public — no wallet, no signup." },
        { title: "A crypto wallet", body: "To claim and own, you need a wallet: a small, free browser app that proves it's you. We never hold your keys or funds." },
        { title: "Test funds, not real money", body: "The Grid runs on Base Sepolia, a free test network today. You use test USDC to try everything safely." },
        { title: "A few minutes", body: "Claiming, designing and publishing take minutes — not hours." },
      ],
    },
    journey: {
      eyebrow: "End to end",
      title: "The complete journey",
      intro: "The whole loop at a glance. Each step leads naturally into the next.",
      optional: "optional",
      steps: ["Explore", "Claim", "Design", "3D layer", "Publish", "Get discovered"],
    },
    draft: {
      eyebrow: "Two versions",
      title: "Draft vs Published",
      intro: "Your work always lives in two places. Knowing the difference is the whole trick.",
      draftLabel: "Draft",
      draftBody: "Your private working copy. Only you see it. Edit as much as you want, whenever you want.",
      publishedLabel: "Published",
      publishedBody: "A durable public snapshot. This is what visitors see. It never changes until you publish again.",
      takeaway: "Editing your draft is always safe — the public keeps seeing your last published version.",
    },
    levels: {
      eyebrow: "Three zoom levels",
      title: "How The Grid is experienced",
      intro: "The same territory reveals more the closer you look.",
      items: [
        { tag: "Zoomed out", title: "The map", body: "See the whole finite territory at once and where everything is." },
        { tag: "Zoomed in", title: "A page", body: "Zoom into a parcel and it becomes a real page — the owner's Canvas with text, links and media." },
        { tag: "Deeper (optional)", title: "In 3D", body: "Keep zooming into a parcel that has a 3D layer and you step inside its voxel scene." },
      ],
    },
    faq: {
      eyebrow: "Beginner questions",
      title: "Beginner FAQ",
      intro: "New to The Grid or to Web3? Start here.",
      items: [
        { q: "Do I need a wallet just to look?", a: "No. Exploring the map and opening any published page is completely public and free." },
        { q: "What is a \"wallet\"?", a: "A small app — often a browser extension — that holds your on-chain identity and lets you approve actions. You stay in control; we never hold your keys or funds." },
        { q: "Is this real money?", a: "Not today. The Grid runs on Base Sepolia, a free test network, using test USDC — so you can try everything safely." },
        { q: "What are cells and parcels?", a: "The Grid is one map of 1,000,000 small squares called cells. A parcel is a rectangle of cells that you own." },
        { q: "Can I change my page after publishing?", a: "Yes. You keep a private draft and can edit anytime. The public keeps seeing your last published version until you publish again." },
        { q: "What is Grid-Power?", a: "A simple budget based on your land size that limits how much you can place, keeping pages honest and fast to load." },
      ],
    },
  },
  explore: {
    eyebrow: "Discover",
    titleLine1: "PUBLISHED",
    titleHighlight: "TERRITORIES.",
    intro:
      "Real, currently-live territories on THE GRID — newest first. No pay-to-rank, no invented trending, no fake counts. Open any one to see its public page.",
    error: {
      title: "Couldn't load the public directory",
      body: "The discovery service is unavailable right now. Please try again shortly.",
      retry: "Retry",
    },
    empty: {
      title: "The map is waiting",
      body: "No territory has been published yet. When creators publish their Canvas, they will appear here — newest first. You could be the first.",
      claim: "Claim your territory",
      how: "How it works",
    },
    liveOne: "{count} live territory",
    liveOther: "{count} live territories",
    modulesOne: "{count} module",
    modulesOther: "{count} modules",
    untitled: "Untitled Territory",
    view: "View territory",
    badgeMedia: "Media",
  },
  creators: {
    eyebrow: "Creators & Brands",
    titleLine1: "YOUR TERRITORY,",
    titleHighlight: "YOUR STAGE.",
    intro:
      "Whatever you make, there is a starting point for it. Six premium Canvas templates turn an owned territory into a finished page in minutes — then you edit every pixel. Templates load into your own draft inside the editor; nothing is published until you choose to.",
    sixTemplates: "Six premium templates",
    useCases: {
      creator: "For makers building a personal brand — one bold identity, bio and a single call to action.",
      retail: "For small shops & brands — a header over a product shelf with a clear route to buy.",
      gallery: "For artists — the work leads, a quiet statement follows.",
      music: "For releases & live events — title, date, place and a direct tickets / stream link.",
      product: "For launches — headline, supporting line, three feature blocks and a CTA.",
      minimal: "For high-end minimalism — restraint, whitespace and one considered action.",
    },
    noteBefore: "Templates live inside the Canvas editor for a territory you own. Open a territory, hit ",
    noteBold: "Templates",
    noteAfter: ", pick a starting point — it fills your draft, and Save / Publish stay in your hands.",
    howCta: "How it works",
  },
  rent: {
    eyebrow: "Planned feature",
    titleLine1: "PARCEL",
    titleHighlight: "RENT",
    intro: "A planned way for territory owners to rent only part of their territory to brands for temporary promotional campaigns — while keeping full ownership of their parcel.",
    release: "Planned Release: Q1 2027",
    status: "Not available yet · Informational preview",
    coexist: {
      eyebrow: "Shared territory",
      title: "One territory. Two separate areas.",
      body: "The owner keeps their own space and identity. A sponsor's campaign would live only inside the rented area, for a limited time — the two coexist side by side, never mixed.",
      ownerLabel: "Owner",
      sponsorLabel: "Sponsor",
      ownerLegend: "Owner area — stays with the owner",
      sponsorLegend: "Sponsor area — rented, time-limited",
      caption: "Illustrative example — not a real territory or campaign",
    },
    how: {
      eyebrow: "How it is planned to work",
      steps: [
        { title: "Ownership stays", body: "The parcel and its NFT never change hands during a rental." },
        { title: "Only part is rented", body: "The owner chooses which area to offer; the rest of the territory stays untouched." },
        { title: "Temporary campaigns", body: "A sponsor could publish only inside the approved area, and access ends when the rental period expires." },
        { title: "Verified & official", body: "Planned identity checks for RENT participants and payments through The Grid's official rental flow." },
      ],
    },
    model: {
      eyebrow: "Proposed business model",
      title: "Rental income for owners. A commission for The Grid.",
      body: "Under the proposed model, the territory owner may earn rental income from the rented area, while The Grid would receive a commission on each official rental transaction.",
      cards: [
        { title: "Owners", body: "Potential rental income from part of a territory, without selling the parcel." },
        { title: "Brands", body: "A temporary promotional presence inside selected creator territories." },
        { title: "The Grid", body: "A commission on official rental transactions." },
      ],
      note: "Commission levels, pricing and rental terms have not been defined yet.",
    },
    disclaimer: "Parcel RENT is a planned feature and is not currently available. No booking, payment or rental can be made today. Details, eligibility, pricing and launch timing are subject to change; no earnings, traffic or results are guaranteed.",
  },
  shellCta: {
    title: "The map is public. Ownership is not.",
    body: "Explore every published territory freely. When you are ready to claim a position, sign in with your wallet — the ledger will remember it.",
    explore: "Explore territories",
    enterGrid: "Enter the Grid",
  },
  onboarding: {
    profileEntry: { eyebrow: "Guided setup" },
    eyebrow: "Guided setup",
    title: "Launch your territory",
    intro: "A guided path from zero to a published territory. Nothing is forced — leave any time and pick up where you left off. Each step sends you to the real tool, and your progress is saved.",
    authRequired: {
      title: "Sign in to start the guide",
      body: "The guided setup is tied to your wallet so your progress is saved. Connecting a wallet is not enough — a signed session is required.",
      cta: "Sign in with Ethereum",
    },
    loading: "Loading your guide…",
    error: {
      title: "Couldn't load your guide",
      body: "The guide service is unavailable right now. Please try again shortly.",
      retry: "Retry",
    },
    progress: {
      label: "Step {n} of {total}",
      complete: "{done} of {total} done",
      statusDone: "Done",
      statusNow: "Now",
      statusNext: "Next",
      statusLocked: "Later",
    },
    restart: "Restart guide",
    restartConfirm: "Restart the guide? This resets only your guided progress — your territories, drafts and publications are never touched.",
    ownershipLost: "The territory you selected is no longer owned by this wallet. We took you back to territory selection.",
    actions: {
      select: "Select",
    },
    personas: {
      selected: "Selected",
      creator: {
        label: "Creator",
        desc: "A personal brand, portfolio or a single bold identity with one clear call to action.",
      },
      retail: {
        label: "Business / Retail",
        desc: "A shop, product or brand — a clear header over what you offer and a route to act.",
      },
    },
    steps: {
      persona: {
        title: "Who are you?",
        body: "Tell us whether you are a Creator or a Business. We tailor the starting points to match — you can change this any time.",
      },
      territory: {
        title: "Get your territory",
        body: "Own a piece of the Grid, then pick which territory this guide will build.",
        pick: "Pick the territory to build",
        selectedLabel: "Building",
        needLand: "You don't own any land yet. Claim a parcel on the Grid to get started.",
        getLand: "Get land on the Grid",
        nameIt: "Name this territory",
        loading: "Resolving your territories…",
        unavailable: "Your ownership data couldn't be resolved right now. Please try again shortly.",
      },
      start: {
        title: "Choose how to start",
        body: "Start from a blank Canvas, or load one of six premium templates and make it yours. Both open the real editor — nothing is published until you choose to.",
        blank: "Start blank",
        template: "Use a template",
        hint: "Templates live inside the editor under \"Templates\".",
      },
      build: {
        title: "Build your territory",
        body: "Design your Canvas: identity, media, links and contacts. Save your draft when it looks right — it stays private until you publish.",
        open: "Open the Canvas editor",
        draftSaved: "Draft saved",
        noDraft: "No draft saved yet",
      },
      publish: {
        title: "Publish",
        body: "Publishing takes a durable public snapshot of your saved draft. You can keep editing privately afterwards; the public sees your published version until you republish.",
        open: "Open publish",
        published: "Published",
        notReady: "Save a draft first, then publish from your territory.",
      },
      launch: {
        title: "Launch & grow",
        body: "Your territory is live. Share it — it appears on Explore automatically, newest first, with no pay-to-rank.",
        viewPublic: "View your public page",
        explore: "See it on Explore",
        copyLink: "Copy public link",
        copied: "Link copied",
        locked: "Publish your territory to unlock this step.",
      },
    },
    guide: {
      title: "Guided setup",
      heading: "Build your territory",
      intro: "Add your identity, visuals and links, then save your draft.",
      items: {
        identity: "Add your identity (title, tagline)",
        media: "Add a visual or media",
        social: "Add social / contact info",
        save: "Save draft",
      },
      back: "Back to guide",
      hide: "Hide guide",
    },
    entry: {
      eyebrow: "New here?",
      title: "Let's launch your first territory",
      body: "A short guided path takes you from claiming land to a published page. Nothing is forced.",
      start: "Start the guide",
      notNow: "Not now",
      resume: "Resume guide",
      restart: "Restart guide",
      completed: "Guide complete",
    },
    creatorsCta: {
      title: "New to THE GRID? Take the guided path.",
      body: "Go from an owned territory to a published page in a few guided steps — templates, build, publish.",
      cta: "Start the guided setup",
    },
  },
  marketplace: {
    home: {
      eyebrow: "The Grid · Marketplace",
      title: "Own, trade & collect the coordinates of a living world.",
      subtitle: "Every listing is backend-authoritative and fail-closed. Land parcels and frozen artworks settle through the same on-chain settlement authority — nothing is ever fabricated.",
      browseLand: "Browse Land",
      browseArtworks: "Browse Artworks",
      cardLandTitle: "Land",
      cardLandDesc: "Fixed-price parcels with zone-authoritative pricing and cross-currency floors.",
      cardArtworksTitle: "Artworks",
      cardArtworksDesc: "Frozen, placed artworks trading with creator royalties and live auctions.",
      cardMineTitle: "My Market",
      cardMineDesc: "Your listings, offers made & received, and auction bids in one place.",
    },
    nav: {
      search: "Search",
      searchAria: "Search the marketplace",
      notifications: "Notifications",
      notificationsCountAria: "{count} notifications",
    },
    search: {
      title: "Search the marketplace",
      subtitle: "Parcels · artworks · addresses",
      placeholder: "Parcel #, artwork title, or wallet address…",
      startTyping: "Start typing to search listings and artworks.",
      noMatches: "No matches for “{q}”.",
      land: "land",
      artwork: "artwork",
      sellerPrefix: "Seller {addr}",
      byPrefix: "by {addr}",
      auctionSuffix: " · auction",
    },
    notifications: {
      title: "Notifications",
      offersOne: "{n} offer to review",
      offersMany: "{n} offers to review",
      offersDetail: "Buyers have made offers on your assets.",
      outbidOn: "Outbid on {title}",
      anAuction: "an auction",
      topBid: "Top bid {top} · your bid {yours}",
      allCaughtUp: "You're all caught up.",
      markAll: "Mark all read",
    },
    land: {
      sectionTitle: "Land Listings",
      loadErrorTitle: "Couldn't load listings",
      loadErrorDetail: "The marketplace index is unreachable right now. Nothing was changed.",
      emptyNoneTitle: "No land listed yet",
      emptyNoneHint: "When owners list parcels for sale, they appear here.",
      emptyFilteredTitle: "No listings match your filters",
      emptyFilteredHint: "Try widening the price range or clearing the zone filter.",
      slots: "{n} slots",
      filters: {
        zone: "Zone",
        price: "Price (USDC)",
        min: "Min",
        max: "Max",
        minSlots: "Min slots",
        any: "Any",
        sort: "Sort",
        clear: "Clear filters",
        sortRecent: "Most recent",
        sortPriceAsc: "Price · low to high",
        sortPriceDesc: "Price · high to low",
        sortSlotsDesc: "Largest first",
      },
    },
    zones: {
      all: "All zones",
      standard: "Standard",
      premium: "Premium",
      vip: "VIP",
      reserve: "Reserve",
      gridZero: "Grid Zero",
    },
    artworks: {
      loadErrorTitle: "Couldn't load artworks",
      loadErrorDetail: "The gallery index is unreachable right now.",
      liveAuctions: "Live Auctions",
      live: "{n} live",
      gallery: "Gallery",
      works: "{n} works",
      emptyTitle: "No artworks yet",
      emptyHint: "Frozen artworks placed on the Grid appear here for offers and auctions.",
      auction: "Auction",
      openToOffers: "Open to offers",
      royalty: "{n}% royalty",
      by: "by {addr}",
    },
    detail: {
      backToLand: "Back to Land",
      backToArtworks: "Back to Artworks",
      listingNotFound: "Listing not found",
      listingNotFoundDetail: "This listing may have sold or been withdrawn.",
      artworkNotFound: "Artwork not found",
      artworkNotFoundDetail: "This artwork may have been moved or removed.",
      loadingParcel: "Loading parcel…",
      loadingArtwork: "Loading artwork…",
      seller: "Seller {addr}",
      creatorOwner: "by {creator} · owner {owner}",
      slots: "Slots",
      footprint: "Footprint",
      floor: "Floor",
      size: "Size",
      cells: "Cells",
      royalty: "Royalty",
      status: "Status",
      yourListing: "Your listing",
      buyNow: "Buy now",
      unavailable: "Unavailable",
      makeOffer: "Make offer",
      viewOnGrid: "View on Grid",
      sellerNote: "You are the seller — buying and offers are disabled on your own listing.",
      ownerNote: "You own this artwork — offers and bids are disabled for you.",
      openOffers: "Open Offers",
      noOffersParcel: "No open offers on this parcel.",
      noOffersArtwork: "No open offers on this artwork.",
      auctionTag: "Auction",
      openToOffers: "Open to offers",
      currentBid: "Current bid",
      reserve: "Reserve",
      leadingPrefix: "Leading {addr} · ",
      noBidsYet: "No bids yet · ",
      minNext: "min next {amt} {sym}",
      auctionEnded: "Auction ended",
      yourAuction: "Your auction",
      placeBid: "Place bid",
      buyTitle: "Buy Parcel #{id}",
      buySubtitle: "Fixed-price settlement",
      done: "Done",
      cancel: "Cancel",
      reserving: "Reserving…",
      authorizing: "Authorizing…",
      reserveAuthorize: "Reserve & authorize",
      signInRequired: "Sign in required",
      connectSignIn: "Connect your wallet & sign in →",
      settlementUnavailable: "Settlement is temporarily unavailable — nothing can be charged right now.",
      price: "Price",
      gridFee: "Grid fee",
      settlementAuthorized: "Settlement authorized",
      settlementAuthorizedBody: "The settlement authority signed your permit. Complete the purchase from your wallet to finalize on-chain.",
      authorityFloor: "Authority {addr} · floor {amt} {sym}",
      completePurchase: "Complete purchase",
      checkingAllowance: "Checking allowance…",
      confirmInWallet: "Confirm in your wallet…",
      approvingUsdc: "Approving USDC…",
      settlingOnchain: "Finalizing on-chain…",
      purchaseComplete: "Purchase complete",
      purchaseCompleteBody: "The LAND is now yours. Ownership and listings will refresh shortly.",
      buyFailed: "Purchase failed",
      approveRejected: "USDC approval was rejected in your wallet.",
      approveReverted: "The USDC approval transaction reverted on-chain.",
      fillRejected: "The purchase was rejected in your wallet.",
      fillReverted: "The purchase transaction reverted on-chain.",
      wrongBuyerWallet: "Connect the buyer wallet {addr} to complete the purchase.",
      makeOfferTitle: "Make an offer",
      makeOfferSubtitle: "Binding offer · market-time expiry",
      submitOffer: "Submit offer",
      submitting: "Submitting…",
      amount: "Amount",
      expiresIn: "Expires in",
      dur1h: "1 hour",
      dur24h: "24 hours",
      dur3d: "3 days",
      dur7d: "7 days",
      placeBidTitle: "Place a bid",
      auctionFallback: "Auction",
      signing: "Signing…",
      yourBid: "Your bid ({sym})",
      minNextBid: "Minimum next bid: {amt} {sym}",
      bidPermitSigned: "Bid permit signed",
      bidPermitBody: "The settlement authority signed your bid permit. Submit it from your wallet to record the bid on-chain.",
      antiSnipe: " Your bid falls inside the anti-snipe window and will extend the auction.",
      authorityMin: "Authority {addr} · min accepted {amt} {sym}",
    },
    provenance: {
      title: "History / Provenance",
      empty: "No ownership history yet for this parcel.",
      resale: "Resale",
      primaryPurchase: "Primary purchase",
      privateSale: "Private sale",
      transfer: "Transfer",
      from: "From",
      to: "To",
      price: "Price",
      fee: "Fee",
      net: "Net",
      viewTx: "View tx →",
      currentNameLabel: "Current display name",
    },
    owner: {
      cancelPrivateSale: "Cancel",
      cancelBlocked: "Not cancellable",
      cancelUnsupportedNote: "On-chain private-sale cancellation is not supported by the deployed contract. Cancellation requests fail closed and never modify the record.",
      cancelPsTitle: "Cancel private sale",
      cancelPsBadge: "Owner action",
      cancelPsWarning: "This requests on-chain cancellation of the private sale. It only takes effect once the transaction is confirmed on-chain.",
      cancelPsSuccess: "Private sale cancelled on-chain.",
      cancelPsConfirm: "Request cancellation",
      cancelPsClose: "Close",
      cancelPsUnsupported: "The deployed contract does not support on-chain private-sale cancellation. No record was modified.",
      psStatusActive: "Active",
      psStatusCompleted: "Completed",
      psStatusExpired: "Expired",
      psStatusCancelled: "Cancelled",
      verifyingAccess: "Verifying owner access…",
      accessDeniedTitle: "Owner access required",
      accessDeniedHint: "This console is restricted to server allow-listed owner wallets. Sign in with an admin wallet.",
      loadErrorTitle: "Couldn't load the owner console",
      loading: "Loading owner console…",
      integrityTitle: "Supply Integrity",
      noFreeGrant: "No free grant ✓",
      grantAnomaly: "Grant anomaly ✕",
      supplyActiveListings: "Active Listings",
      supplySoldListings: "Sold Listings",
      supplyOpenOffers: "Open Offers",
      supplyOpenAuctions: "Open Auctions",
      supplyPendingSettlements: "Pending Settlements",
      supplyPaidAcquisitions: "Paid Acquisitions",
      zeroPriceGrants: "Zero-price grants",
      actionsTitle: "Owner Actions",
      gridZeroManual: "Grid Zero manual",
      releaseLabel: "Release",
      releaseDesc: "Make eligible inventory available. Cell-authoritative: released, not effectively reserved, not occupied.",
      relockLabel: "Relock",
      relockDesc: "Relock available inventory. Never relocks sold, occupied or otherwise ineligible cells.",
      privateSaleLabel: "Private Sale",
      privateSaleDesc: "Create a buyer-scoped sale. Tier price is official; Grid Zero is manual. Price must be > 0.",
      failClosed: "Fail-closed",
      unavailable: "Unavailable",
      privateSalesTitle: "Private Sales",
      noPrivateSalesTitle: "No private sales projected",
      noPrivateSalesHint: "Private sales appear here once created on-chain — cancellable until pending.",
      buyer: "buyer {addr}",
      perSlot: "/ slot",
      executed: "Executed",
      pending: "Pending",
      supplyCheckTitle: "Supply Check",
      walletPlaceholder: "0x wallet address",
      checking: "Checking…",
      auditWallet: "Audit wallet",
      supplyCheckFailed: "Supply check failed",
      owned: "Owned",
      paidOrigin: "Paid origin",
      integrity: "Integrity",
      consistent: "Consistent",
      anomalies: "Anomalies",
      confirmationSuffix: "{tier} confirmation",
      tierNormal: "normal",
      tierStrong: "strong",
      tierMaximum: "maximum",
      failClosedBanner: "Fail-closed: {reason}. This action cannot be executed — nothing is charged or fabricated.",
      typeToProceedPre: "Type ",
      typeToProceedPost: " to proceed",
      ack: "I understand this is a cell-authoritative owner action and confirm the target and quantity.",
      confirmAction: "Confirm {action}",
      submitting: "Submitting…",
      cancel: "Cancel",
      zoneLabel: "Zone",
      slotsLabel: "Slots",
      buyerLabel: "Buyer wallet",
      priceLabel: "Unit price (USDC)",
      priceHintGridZero: "Grid Zero requires a manual unit price > 0.",
      priceHintTier: "Tier price is official and applied automatically.",
      actionSuccess: "Owner action executed",
      actionSuccessBody: "{action}: {count} cells in {zone} — recorded to the immutable owner audit. No free grant, no chain write.",
      demoScope: "Preview Demo — isolated substrate, no on-chain write",
    },
    common: {
      parcelNumber: "Parcel #{id}",
      partialTag: "PARTIAL",
      close: "Close",
      retry: "Retry",
    },
    confirm: {
      normal: "Please confirm this cell-authoritative owner action.",
      strong: "This affects {slots} slots (over {threshold}). Re-enter the exact slot count to proceed.",
      maximum: "GRID ZERO is the most sensitive inventory on THE GRID. Maximum confirmation is required regardless of quantity.",
    },
    statePill: {
      active: "active",
      sale_pending: "sale pending",
      confirming: "confirming",
      sold: "sold",
      expired: "expired",
      cancelled: "cancelled",
      reconciliation_required: "reconciliation required",
      open: "open",
    },
    errors: {
      unauthenticatedTitle: "Sign in required",
      unauthenticatedHint: "Connect your wallet and sign in to authorize this action.",
      disabledTitle: "Marketplace paused",
      disabledHint: "Settlement is temporarily unavailable. Nothing was charged or changed — try again once it resumes.",
      cellLockedTitle: "Reserved by another buyer",
      cellLockedHint: "One or more cells in this parcel are held by another buyer's pending purchase right now. Try again in a few minutes.",
      notActiveTitle: "No longer available",
      notActiveHint: "This listing is no longer active — it may have just sold or been withdrawn. Refresh to see the latest.",
      expiredTitle: "Listing expired",
      expiredHint: "This listing has expired and can no longer be purchased. The owner can relist it.",
      selfTitle: "This is your listing",
      selfHint: "You can't buy or bid on an asset you're already selling.",
      belowFloorTitle: "Below floor price",
      belowFloorHint: "The price moved and this is now below the seller's floor. Review and try again.",
      settlementTitle: "Settlement in progress",
      settlementHint: "A settlement for this asset is already in progress. Wait for it to finalize before trying again.",
      reconciliationTitle: "Reconciliation required",
      reconciliationHint: "This asset must be reconciled with the chain before it can trade. No action was taken.",
      spatialTitle: "Placement conflict",
      spatialHint: "This artwork isn't in a settleable placement right now — it may have moved or overlap another placement.",
      bidLowTitle: "Bid too low",
      bidLowHint: "Your bid is below the minimum next bid. Increase it and try again.",
      permitReusedTitle: "Permit already used",
      permitReusedHint: "That bid permit was already spent. Place a fresh bid.",
      auctionClosedTitle: "Auction closed",
      auctionClosedHint: "This auction is no longer accepting bids.",
      auctionNotStartedTitle: "Auction not started",
      auctionNotStartedHint: "This auction hasn't opened for bids yet.",
      sellerBidTitle: "Sellers can't bid",
      sellerBidHint: "You can't bid on your own auction.",
      permitRefusedTitle: "Authorization refused",
      permitRefusedHint: "The settlement authority declined to sign. No action was taken.",
      noBasisTitle: "Origin not recorded",
      noBasisHint: "This asset has no recorded PAID acquisition origin yet, so it can't be settled. No action was taken.",
      zeroAmountTitle: "Amount required",
      zeroAmountHint: "Enter an amount greater than zero.",
      genericTitle: "Something went wrong",
      genericHint: "The action could not be completed. Nothing was changed.",
    },

    tabs: {
      overview: "Overview",
      land: "Land",
      artworks: "Artworks",
      mine: "My Market",
      owner: "Owner Console",
      ownerAria: "Owner Console — admin only",
    },
    states: {
      loading: "Loading…",
      retry: "Retry",
      active: "active",
    },
    paused: {
      title: "Marketplace paused",
      body: "Settlement is temporarily unavailable. Browsing stays open; nothing can be charged.",
    },
    mine: {
      signInTitle: "Sign in to see your marketplace",
      signInHint: "Connect your wallet and sign in to view your listings, offers and bids.",
      connectSignIn: "Connect & sign in →",
      loadErrorTitle: "Couldn't load your marketplace",
      loadingYours: "Loading your market…",
      listings: "My Listings",
      listLand: "List Land",
      noListings: "No active listings",
      noListingsHint: "List a parcel from its detail page — or use List Land — to sell it.",
      offersReceived: "Offers Received",
      offersMade: "Offers Made",
      bids: "My Bids",
      noBids: "No active bids",
      noBidsHint: "Bid on a live auction to see it here.",
      cancel: "Cancel",
      withdraw: "Withdraw",
      noOpenOffers: "No open offers",
      noOffersReceived: "No offers received",
      offerMadeHint: "Make an offer from any listing or artwork.",
      offerReceivedHint: "Offers on your assets will appear here.",
      accept: "Accept",
      decline: "Decline",
      accepting: "Accepting…",
      acceptUnsupported: "This offer type cannot be accepted here yet.",
      pendingPurchases: "Pending Purchases",
      noPendingPurchases: "No pending purchases",
      pendingPurchasesHint: "Offers a seller accepted appear here to complete the purchase.",
      completePurchase: "Complete purchase",
      auction: "Auction",
      leading: "Leading",
      outbid: "Outbid",
      bidPrefix: "bid",
      artworkLabel: "Artwork",
      sales: "Sales",
      purchases: "Purchases",
      noSales: "No sales yet",
      noSalesHint: "Parcels you sell on the secondary market will appear here.",
      noPurchases: "No purchases yet",
      noPurchasesHint: "Parcels you buy on the secondary market will appear here.",
      soldTo: "Sold to",
      boughtFrom: "Bought from",
      feeShort: "Fee",
      netShort: "Net",
      soldStatus: "Sold",
      viewTx: "View tx →",
    },
    sell: {
      slotsSelected: "Selected slots",
      partialBadge: "Partial area",
      realSelectTitle: "Select the area to sell",
      realSelectHint: "Click or drag to select cells. Select the whole parcel to list it, or a contiguous area for a partial sale.",
      selectWhole: "Select whole parcel",
      wholeSelectedNote: "Whole parcel selected — this lists the entire parcel.",
      wholeIneligibleTitle: "Whole-parcel sale unavailable",
      wholeIneligibleBody: "Part of this parcel is ineligible — a live marketplace lock or canonical content (a placed artwork) occupies one or more cells. The whole-parcel listing is blocked until those cells are free. No listing was created.",
      partialDisabledTitle: "Partial sale unavailable",
      partialVaultRequired: "A partial area sale requires an on-chain SubdivisionVault child token for this parcel, which does not exist yet. Create the subdivision on-chain before listing this area. No listing was created.",
      subdivisionTooComplex: "The selected shape exceeds the on-chain subdivision limit ({total}/{max} rectangles) — adjust your selection. No transaction was sent.",
      subAlreadySubdivided: "This parcel is already subdivided on-chain. Re-subdivision is not supported yet.",
      subDepositFailed: "Depositing the parcel into the vault failed.",
      subSplitFailed: "The on-chain subdivision split failed.",
      subGeometryMismatch: "The on-chain child geometry does not match your selection.",
      sub_plan: "Computing subdivision…",
      sub_approve: "Approving vault…",
      sub_approveCollection: "Approving marketplace…",
      nftApprovalFailed: "Marketplace transfer approval failed. No listing was created.",
      sub_deposit: "Depositing parcel…",
      sub_split: "Subdividing on-chain…",
      sub_reconcile: "Reconciling…",
      sub_sign: "Sign & list…",
      wholeTitle: "List the whole parcel",
      wholeSubtitle: "Parcel #{id} · whole parcel",
      wholeBadge: "Whole parcel",
      wholeIntro: "You are authorizing a REAL secondary-market listing of the ENTIRE parcel on Base Sepolia. Your wallet signs an EIP-712 order; nothing is written on-chain until a buyer fills it.",
      wholeConnectRequired: "Connect the owner wallet to authorize this listing.",
      wholeOwnerMismatch: "The connected wallet is not the on-chain owner of this parcel.",
      wholeNotConfigured: "The marketplace contract is not configured for real listings.",
      signAndList: "Sign & list",
      signing: "Awaiting wallet signature…",
      reviewToken: "Token ID",
      reviewCollection: "Collection",
      realCreatedTitle: "Listing authorized",
      realCreatedBody: "Your signed order is live in the marketplace. It settles on-chain only when a buyer fills it.",
      partialFutureTitle: "Sell a subset (partial)",
      partialFutureBody: "Partial sale requires an on-chain subdivision-vault child token, which is not yet available on Base Sepolia. Only whole-parcel listings are possible right now.",
      priceRealHelp: "Set your asking price in {sym}.",
      listLand: "List Land",
      sellLand: "Sell Land",
      sellLandDemo: "Sell Land · Preview Demo",
      previewDemo: "Preview Demo",
      previewDemoGrid: "Preview Demo Grid",
      myLand: "My Land",
      selectLand: "Select Land",
      pickTitle: "Choose land to sell",
      pickHint: "Select one of your parcels to list a contiguous cell-subset for sale.",
      pickEmpty: "No sellable land",
      pickEmptyHint: "You don't own any land available for partial sale in this preview.",
      pickCells: "{n} cells",
      pickListed: "{n} active",
      pickOpen: "Select area →",
      wizardTitle: "Sell Land",
      wizardSubtitle: "Parcel #{id} · Preview Demo",
      stepSelect: "Select area",
      stepPrice: "Price",
      stepReview: "Review",
      selectTitle: "Select the cells to sell",
      selectHint: "Click a cell to toggle it, or click-and-drag to paint a rectangle. The selection must be one contiguous block and cannot be the whole parcel.",
      selectedCount: "{n} of {total} cells selected",
      clearSelection: "Clear",
      valLoading: "Validating…",
      valValid: "Selection valid",
      valInvalid: "Selection not valid",
      basisLabel: "Acquisition basis",
      floorLabel: "Minimum resale floor (80%)",
      floorBinding: "Binding",
      floorNotBinding: "Floor not binding (no recorded basis)",
      reasonOutside: "Cells fall outside the parcel",
      reasonNotContiguous: "Cells must form one contiguous block",
      reasonFull: "That's the whole parcel — use the whole-parcel listing",
      reasonNotOwner: "You are not the owner of this land",
      reasonOverlap: "Some cells overlap an existing listing",
      reasonOccupied: "Some cells are occupied by canonical content",
      reasonNoBasis: "Resale floor unavailable: no acquisition cost basis is recorded for this parcel.",
      priceTitle: "Set your price",
      priceLabel: "Price",
      currencyLabel: "Currency",
      priceHelpFloor: "Must be at least the {floor} {sym} floor.",
      belowFloor: "Price is below the {floor} {sym} minimum floor.",
      reviewTitle: "Review & authorize",
      reviewCells: "Cells",
      reviewArea: "Area",
      reviewFloor: "Floor",
      reviewPrice: "Your price",
      authorize: "Authorize & List",
      authorizeDemo: "Authorize (Preview Demo) & List",
      creating: "Creating listing…",
      createdTitle: "Demo listing created",
      createdBody: "Your preview demo listing is now visible in My Listings.",
      viewMine: "View My Listings",
      demoDisclosureTitle: "Preview Demo — no on-chain / canonical change",
      demoDisclosureBody: "This lists an isolated demo parcel on the preview substrate. Your SIWE session authorizes the write; no on-chain SubdivisionVault mint, EIP-712 order or Base Sepolia settlement happens. In production this same flow signs your wallet and settles on-chain.",
      back: "Back",
      next: "Next",
      close: "Close",
    },
    artworkMkt: {
      buyNow: "Buy now",
      listedAt: "Listed at",
      fixedPrice: "Fixed price",
      yourListing: "Your listing",
      cancelListing: "Cancel listing",
      sellArtwork: "Sell / List Artwork",
      listArtwork: "List / Sell",
      buyTitle: "Buy “{title}”",
      buySubtitle: "Fixed-price artwork settlement",
      buyIntro: "You are buying this artwork at its listed price. Your wallet locks USDC in escrow, then the sale finalizes on-chain. Nothing moves until you confirm.",
      priceLabel: "Price",
      royaltyLabel: "Creator royalty",
      gridFeeLabel: "Grid fee",
      confirmBuy: "Confirm & buy",
      buyPreparing: "Preparing settlement…",
      buyCheckingAllowance: "Checking USDC allowance…",
      buyApprove: "Approve USDC in your wallet…",
      buyApproving: "Approving USDC…",
      buyOpenWallet: "Confirm escrow in your wallet…",
      buyOpening: "Locking funds in escrow…",
      buyIndexingEscrow: "Confirming escrow on-chain…",
      buyAuthorizing: "Authorizing payout…",
      buyReleaseWallet: "Confirm payout in your wallet…",
      buyReleasing: "Releasing payout on-chain…",
      buyIndexingPayout: "Confirming payout finality…",
      buyFinalizing: "Transferring ownership…",
      buyComplete: "Purchase complete",
      buyCompleteBody: "The artwork is now yours. Ownership and listings refresh shortly.",
      buyRejected: "The action was rejected in your wallet.",
      buyReverted: "The transaction reverted on-chain.",
      buyFailed: "Purchase failed",
      connectBuyer: "Connect the buyer wallet {addr} to complete this purchase.",
      sellIntro: "You own this artwork. List it at a fixed price or open a live auction.",
      cancelListingConfirm: "Cancel this listing?",
      listingCancelled: "Listing cancelled.",
      wizardTitle: "Sell Artwork",
      pickTitle: "Choose an artwork to sell",
      pickHint: "Select a frozen, placed artwork you own that isn't already listed or in auction.",
      pickEmpty: "No sellable artworks",
      pickEmptyHint: "You need a frozen artwork placed on the Grid that isn't already listed or auctioned.",
      chooseMode: "How do you want to sell?",
      modeFixed: "Fixed price",
      modeFixedDesc: "List at a set price. Buyers purchase instantly; you sign nothing on-chain.",
      modeAuction: "Auction",
      modeAuctionDesc: "Run a timed English auction with a reserve. You sign the auction creation on-chain.",
      setPrice: "Set your price",
      priceHint: "Set your asking price in {sym}. Grid fee {fee}% + creator royalty {roy}% are deducted on settlement.",
      listBtn: "List for sale",
      listing: "Listing…",
      listedTitle: "Artwork listed",
      listedBody: "Your artwork is now listed at the fixed price. Buyers can purchase it from its detail page.",
      setAuction: "Auction terms",
      reserveLabel: "Reserve price",
      minIncrementLabel: "Minimum bid increment",
      durationLabel: "Duration",
      antiSnipeLabel: "Anti-snipe window",
      dur1h: "1 hour",
      dur6h: "6 hours",
      dur24h: "24 hours",
      dur3d: "3 days",
      dur7d: "7 days",
      antiSnipe60: "60 seconds",
      antiSnipe300: "5 minutes",
      antiSnipeOff: "Off",
      createAuctionBtn: "Create auction",
      creatingAuction: "Creating auction…",
      auctionWalletConfirm: "Confirm auction in your wallet…",
      auctionSubmitting: "Creating auction on-chain…",
      auctionCreatedTitle: "Auction created",
      auctionCreatedBody: "Your auction is live on-chain. Bidders can place bids until it ends.",
      review: "Review",
      reviewArtwork: "Artwork",
      reviewRoyalty: "Royalty",
      reviewMode: "Mode",
      reviewPrice: "Price",
      reviewReserve: "Reserve",
      reviewIncrement: "Increment",
      reviewEnds: "Ends",
      settleAuction: "Settle auction",
      settling: "Settling…",
      settleWalletConfirm: "Confirm settlement in your wallet…",
      settled: "Auction settled",
      cancelAuction: "Cancel auction",
      cancellingAuction: "Cancelling…",
      auctionCancelled: "Auction cancelled.",
      bidPlacedOnchain: "Bid placed on-chain.",
      bidApprove: "Approve USDC in your wallet…",
      bidApproving: "Approving USDC…",
      bidWalletConfirm: "Confirm bid in your wallet…",
      bidSubmitting: "Placing bid on-chain…",
      historyTitle: "History / Provenance",
      historyEmpty: "No sale or auction history yet for this artwork.",
      histPrepared: "Settlement prepared",
      histFinalized: "Sold (fixed price)",
      histAuctionSettled: "Auction settled",
      histFrozen: "Frozen & placed",
      myArtworkListings: "My Artwork Listings",
      noArtworkListings: "No artwork listings",
      noArtworkListingsHint: "List a frozen artwork for sale to see it here.",
      myAuctions: "My Auctions",
      noMyAuctions: "No auctions",
      noMyAuctionsHint: "Create an auction from an artwork you own to see it here.",
      close: "Close",
      cancel: "Cancel",
      back: "Back",
      next: "Next",
      done: "Done",
      viewTx: "View tx →",
      signInFirst: "Connect your wallet & sign in →",
      auctionEnds: "Ends {when}",
    },
    demoGrid: {
      title: "Preview Demo Grid",
      subtitle: "Your demo land · isolated preview substrate",
      disclosure: "This is an isolated PREVIEW DEMO surface — not the canonical Grid. Nothing here touches real parcels, chain ownership or Base Sepolia.",
      empty: "You have no demo land in this preview.",
      emptyHint: "Sign in with the demo seller wallet to see sellable demo land.",
      returnToMarket: "← Back to Marketplace",
      ownedBadge: "Yours",
      listedBadge: "{n} listed",
      clickToSell: "Click a parcel to sell part of it",
      worldLabel: "World 1000 × 1000",
      loadError: "Couldn't load your demo land",
    },
    clock: {
      closing: "closing",
      ended: "Ended",
    },
    grid: {
      cellAria: "cell {x},{y}",
      cellLockedAria: "cell {x},{y} (locked)",
    },
  },
  purchase: {
    intro: "Live quote preview — nothing is charged until you confirm.",
    loading: "loading…",
    bootstrapError: "Failed to load /sale/config or /sale/status: {error}",
    recovery: {
      confirming: "Recovered a pending purchase — confirming on Base…",
      syncing: "Confirmed on-chain — syncing to Grid…",
      failed: "The recovered purchase transaction reverted. No parcel was purchased.",
      degraded: "Network temporarily unavailable — retrying purchase status…",
    },
    sections: {
      contracts: "On-chain contracts",
      saleStatus: "Sale status",
      wallet: "Wallet",
      financial: "USDC balance & allowance",
      approval: "USDC approval",
      purchase: "Purchase",
      indexing: "Indexing",
      quote: "Quote",
    },
    gate: { yes: "YES", no: "NO ({reason})" },
    wallet: {
      connect: "Connect {name}",
      disconnect: "Disconnect",
      wrongChain: "Wallet is on chain {chainId}, sale is on {expectedChainId}. Switch network in your wallet.",
      switching: "Switching…",
      switchTo: "Switch to chain {chainId}",
      switchRejected: "switch rejected",
      switchError: "switch error: {message}",
    },
    financial: {
      blocked: "reads disabled: {reason}",
      readError: "read error",
      noSellableQuote: "— (no sellable quote)",
      insufficient: "insufficient_balance (short by {amount})",
      approval: "approval_required (needs {amount})",
      ready: "ready_for_purchase",
      rpcError: "RPC error while reading USDC state.",
      refetch: "Refetch balance/allowance",
    },
    approve: {
      blocked: "approve disabled: {reason}",
      waitingWallet: "Waiting for wallet…",
      submitting: "Submitting…",
      confirming: "Confirming…",
      cta: "Approve exact USDC",
      rejected: "User rejected the approve transaction. You can try again.",
      reverted: "Approve transaction reverted on-chain.",
      error: "Approve error: {error}",
      confirmed: "Approve confirmed. Allowance refetch and forced re-quote fired.",
    },
    tx: {
      blocked: "purchase disabled: {reason}",
      refreshingQuote: "Refreshing final quote…",
      awaitingWallet: "Confirm in your wallet…",
      submitted: "Submitted…",
      confirming: "Confirming on Base…",
      cta: "Purchase",
      rejected: "User rejected the purchase transaction.",
      reverted: "Transaction reverted",
      revertedReason: "Transaction reverted: {reason}",
      error: "Purchase error: {error}",
      confirmed: "Transaction confirmed on-chain. Syncing to Grid — awaiting backend indexing…",
    },
    indexing: {
      pending: "Syncing to Grid…",
      delayed: "Transaction confirmed on-chain. Backend indexing is taking longer than expected.",
      checkAgain: "Check again",
      success: "Owned · Live. Purchase indexed successfully.",
      buyAnother: "Buy another parcel",
      anomaly: "Indexing anomaly detected ({reason}). The on-chain transaction is confirmed but the indexed record does not match the original purchase snapshot. Please contact support with the transaction hash below.",
    },
    quote: {
      empty: "Draw a rectangle on the grid to fetch a quote.",
      clearSelection: "Clear selection",
      occupiedSelf: "This area overlaps a parcel you already own. Minted land cannot be purchased again.",
      occupiedOther: "This area overlaps land owned by another wallet. Draw over free grid to purchase.",
      occupiedGeneric: "This area overlaps existing parcels and is not purchasable. Draw over free grid.",
      loading: "loading quote…",
    },
    errors: {
      tooWide: "Selection too wide — a single parcel can be at most 250 cells wide. Draw a narrower area.",
      tooHigh: "Selection too tall — a single parcel can be at most 250 cells tall. Draw a shorter area.",
      areaExceeds: "Selection too large — a single parcel can cover at most 10,000 slots (cells){yours}. Draw a smaller area to get a quote.",
      areaExceedsYours: " — yours is {slots} slots",
      outOfBounds: "Selection extends past the edge of the Grid. Keep it fully inside the 1000 × 1000 territory.",
      zeroOrNegative: "Selection is empty — draw a rectangle at least one cell wide.",
      vipCap: "This VIP area exceeds the per-parcel commercial cap. Draw a smaller area.",
      zoneCap: "This area exceeds the remaining capacity for its zone. Draw a smaller area.",
      unexpected: "Unexpected error.",
    },
  },
  profile: {
    badge: { none: "No badge yet" },
    cockpit: {
      title: "Overview",
      available: "Available",
      pendingFunding: "Pending Funding",
      ownedParcels: "Owned parcels",
      influence: "Influence",
      lifetimePts: "lifetime pts",
      slots: "{n} slots",
      progressTo: "Progress to {badge}",
      max: "max",
      referralCode: "Referral code",
      qualified: "{n} qualified",
      pending: "{n} pending",
    },
    myGrid: {
      loading: "Loading your grid…",
      signIn: "Sign in",
      unavailable: "Unavailable.",
      failedToLoad: "Failed to load",
      retry: "Retry",
      title: "My Grid",
      empty: "You don't own any land yet. Acquire a parcel to start earning Neighbor rewards and Influence.",
      parcels: "Parcels",
      totalSlots: "Total slots",
      clusters: "Clusters",
      largestCluster: "Largest cluster",
      slots: "{n} slots",
      parcelMeta: "{w}×{h} · {area} slots",
      edgeNeighbors: "{n} edge neighbors",
    },
    influence: {
      title: "Influence Score",
      lifetimePrivate: "Lifetime points · private",
      ptsTo: "{points} pts to ",
      maxReached: "Top badge reached",
      nextAt: "next at {threshold}",
      publicNote: "Public surfaces show only your badge — never your exact score. No leaderboard.",
    },
    analytics: {
      title: "Analytics",
      retry: "Retry",
      loadError: "Couldn't load your analytics.",
      sections: { land: "Land & Ownership", creator: "Creator & Referral", trends: "Trends", rewards: "Rewards & Influence" },
      kpi: {
        parcelsOwned: "Parcels Owned",
        totalArea: "Total Area",
        primaryAcq: "Primary Acquisitions",
        acqVolume: "Acquisition Volume",
        ownedTerritories: "Owned Territories",
        publishedTerritories: "Published Territories",
        createdArtworks: "Created Artworks",
        qualifiedReferrals: "Qualified Referrals",
        influence: "Influence",
        rewardsAvailable: "Rewards Available",
        totalRewards: "Total Rewards",
        pendingFunding: "Pending Funding",
      },
      cells: "cells",
      referralQ: "Referral qualifications",
      inRange: "{n} in range",
      noReferrals: "No qualified referrals in this range yet.",
      rewardsByType: "Rewards by type",
      influenceGrowth: "Influence growth",
      noInfluence: "No influence in this range yet.",
      notLiveTitle: "Rewards & Influence are not live yet.",
      notLiveBody: "These metrics populate automatically once The Grid Rewards program goes live. Nothing is tracked or estimated until then.",
    },
    activity: {
      title: "Activity",
      markAll: "Mark all read ({n})",
      filters: { all: "All", rewards: "Rewards", land: "Land", artwork: "Artwork", account: "Account" },
      loadError: "Couldn't load your activity.",
      retry: "Retry",
      empty: "No activity yet. Meaningful events on your Grid will appear here.",
      loading: "Loading…",
      loadMore: "Load more",
    },
    rewards: {
      loading: "Loading rewards…",
      failedToLoad: "Failed to load rewards",
      auth: { note: "Sign in to view your Rewards balance and withdrawals.", cta: "Go to sign in" },
      unavailable: "Rewards unavailable.",
      retry: "Retry",
      errors: {
        belowMin: "Amount is below the 350 USDC minimum.",
        exceedsAvailable: "Amount exceeds your Available balance.",
        invalidAmount: "Enter a valid amount.",
        unauthenticated: "Your session expired — sign in again.",
        failed: "Withdrawal failed.",
      },
      balance: {
        title: "Rewards Balance",
        available: "Available",
        pendingFunding: "Pending Funding",
        awaitingVault: "Awaiting Vault funding",
        withdrawalPending: "Withdrawal Pending",
        withdrawn: "Withdrawn",
        expired: "Expired",
        expiryDeadline: "Expiry deadline",
        forfeits: "Available forfeits if unclaimed",
        noTimer: "No active timer",
      },
      withdraw: {
        title: "Withdraw",
        disabledNote: "You need at least {min} Available to withdraw. Current Available: {available}.",
        amountLabel: "Amount (USDC)",
        gasLabel: "Network gas estimate (USDC · optional)",
        allToggle: "Withdraw All Available ({available})",
        serviceFee: "Service fee · 2%",
        gasSeparate: "Gas (separate)",
        estimatedNet: "Estimated net",
        minNote: "Minimum {min} gross. A 2% service fee applies; network gas is charged separately and is only an estimate.",
        belowMin: "Below the 350 USDC minimum.",
        exceeds: "Exceeds your Available balance.",
        submitting: "Submitting…",
        submit: "Withdraw",
        all: "Withdraw All",
        success: "Withdrawal requested — net {net} (fee {fee}, gas {gas}).",
      },
      payout: {
        title: "Need help settling?",
        body: "Notify the team to process your Available balance ({available}). This creates no money and never bypasses funding order.",
        reachTitle: "Reach {min} in Available profits to request a payout",
        sending: "Sending…",
        cta: "Request Payout",
        minHint: "Reach {min} in Available profits to request a payout — you have {available}.",
        alreadyOpen: "A payout request is already open.",
        sent: "Payout request sent to the team.",
        failed: "Could not send request.",
      },
      history: {
        title: "Reward History",
        empty: "No rewards yet. Referral (7%) and Neighbor (3%) rewards appear here as land is acquired.",
        referralProv: "Referral · {pct}% of {gross}",
        neighborProvEdge: "Neighbor · {edge}/{total} edge of a {pct}% pool",
        neighborProvPool: "Neighbor · {pct}% pool",
        funded: "Funded",
        pendingFunding: "Pending funding",
        parcels: "parcels {ids}",
      },
      withdrawals: {
        title: "Withdrawals",
        pending: "Pending",
        withdrawn: "Withdrawn",
        failed: "Failed",
        net: "{net} net",
        gross: "gross {x}",
        fee: "fee {x}",
        gas: "gas {x}",
        failureReason: "· {reason} — returned to Available for retry",
      },
    },
  },
  dashboard: {
    publishability: { published: "Published", publishable: "Publishable", notPublishable: "Not publishable" },
    territory: { fallback: "Territory {ordinal}" },
    route: {
      title: "My Territories",
      subtitle: "Owner command center · derived from your real on-grid property",
      backToGrid: "Back to Grid",
      authTitle: "Authentication required",
      authBody: "Sign in with your wallet to manage your territories. Connecting a wallet is not enough — a signed session is required.",
      signIn: "Sign in with Ethereum",
      detailEmpty: "Select a territory to view its management summary.",
    },
    list: {
      resolving: "Resolving your territories…",
      unavailableTitle: "Territories unavailable",
      unavailableBody: "Your complete ownership data could not be resolved right now. Nothing partial is shown. Please try again shortly.",
      emptyTitle: "No territories yet",
      emptyBody: "You do not own any property on the Grid yet.",
      exploreGrid: "Explore the Grid",
    },
    card: {
      area: "Area",
      gpCapacity: "GP Capacity",
      propertyUnits: "Property Units",
      minNote: "Minimum 400 u² to publish.",
      unreadTitle: "An administrator sent you a moderation message",
      unreadAria: "Unread moderation message",
    },
    detail: {
      territoryArea: "Territory Area",
      gpCapacity: "GP Capacity",
      propertyUnits: "Property Units",
      underlyingUnitIds: "Underlying Unit IDs",
      location: "Location (world bounds)",
      minNote: "This territory is below the 400 u² minimum required to publish.",
    },
    identity: {
      title: "Territory Identity",
      rev: "rev {n}",
      authRequired: "A signed session is required to manage this territory's identity.",
      unavailable: "Identity management is available only for a legitimately owned territory.",
      loading: "Resolving current territory…",
      namePlaceholder: "Display name — name this territory",
      saved: "Saved.",
      saving: "Saving…",
      saveName: "Save Name",
      editCanvas: "Edit Canvas →",
      saveErrors: {
        STALE_TERRITORY: "Your territory changed since this was loaded. It has been refreshed — review and save again.",
        IDENTITY_VERSION_CONFLICT: "This identity was updated elsewhere. It has been refreshed — review and save again.",
        IDENTITY_RECONCILIATION_REQUIRED: "This territory now merges with another named territory. Reconciliation is required before saving.",
        TERRITORY_NOT_PUBLISHABLE: "This territory is below the 400 u² minimum required to publish.",
        TERRITORY_NOT_IDENTITY_ELIGIBLE: "Identity management is available only for a legitimately owned territory.",
        DISPLAY_NAME_INVALID: "That name is not allowed.",
        UNTRUSTED_ORIGIN: "This request was blocked for security. Please reload the page and try again.",
        CSRF_REQUIRED: "Your security token was missing. Please reload the page and try again.",
        CSRF_INVALID: "Your security token expired. Please reload the page and try again.",
        CSRF_FETCH_FAILED: "Could not verify your session. Please reload the page and try again.",
        UNAUTHENTICATED: "Your session expired. Please sign in again to save.",
        UNSUPPORTED_MEDIA_TYPE: "Something went wrong sending your request. Please reload and try again.",
        SEED_NOT_OWNED: "You no longer own this territory.",
      },
    },
    reconciliation: {
      intro: "This territory now merges {n} named territories into one. Choose which identity survives — the others become superseded. Names are not merged.",
      metaOne: "{n} unit · rev {rev}",
      metaOther: "{n} units · rev {rev}",
      reconciling: "Reconciling…",
      confirmSurvivor: "Confirm survivor",
      errors: {
        STALE_TERRITORY: "Your territory changed since this was loaded. It has been refreshed — review and choose again.",
        IDENTITY_VERSION_CONFLICT: "One of these identities changed elsewhere. It has been refreshed — review and choose again.",
        RECONCILIATION_NOT_REQUIRED: "This territory no longer needs reconciliation. It has been refreshed.",
        SURVIVOR_NOT_CANDIDATE: "That identity is no longer eligible. It has been refreshed.",
        TERRITORY_NOT_PUBLISHABLE: "This territory is below the 400 u² minimum required to publish.",
        UNTRUSTED_ORIGIN: "This request was blocked for security. Please reload the page and try again.",
        CSRF_REQUIRED: "Your security token was missing. Please reload the page and try again.",
        CSRF_INVALID: "Your security token expired. Please reload the page and try again.",
        CSRF_FETCH_FAILED: "Could not verify your session. Please reload the page and try again.",
        UNAUTHENTICATED: "Your session expired. Please sign in again.",
        TRANSACTION_UNAVAILABLE: "The service is briefly unavailable. Please try again in a moment.",
        SEED_NOT_OWNED: "You no longer own this territory.",
      },
    },
    brandImage: {
      title: "Brand Image · LOD0",
      rev: "rev {n}",
      desc: "Optional map image for this territory (PNG · JPEG · WebP, ≤ 5 MiB, ≤ 2048px). Free.",
      recommendedPrefix: "Recommended for your territory:",
      size: "{rw} × {rh} px",
      ratioNote: "(ratio {ratio}) — matches your parcel so the logo isn't stretched.",
      previewAlt: "Territory brand image",
      unsaved: "Unsaved preview — press Save to keep it.",
      empty: "No brand image yet.",
      saved: "Saved.",
      removed: "Brand image removed.",
      authRequired: "A signed session is required to manage the brand image.",
      chooseAria: "Choose a brand image",
      replace: "Replace…",
      select: "Select image…",
      saving: "Saving…",
      save: "Save",
      cancel: "Cancel",
      remove: "Remove",
      removeConfirm: "Remove brand image?",
      removing: "Removing…",
      confirm: "Confirm",
      keep: "Keep",
    },
    publish: {
      publishing: "Publishing…",
      republish: "Republish",
      published: "Published",
      publish: "Publish",
      blockedSave: "Save your draft before publishing",
      stateLabels: {
        UNPUBLISHED: "Not published",
        PUBLISHED_CURRENT: "Published · current",
        DRAFT_CHANGED: "Published · draft changed",
        TERRITORY_CHANGED: "Published · territory changed",
      },
    },
    media: {
      fileTooLarge: "File is larger than the 250 MiB maximum.",
      title: "Media Library",
      subtitle: "Your wallet's reusable media · Image 10 · GIF 20 (10s) · PDF 25 · Video 250 MiB · Library 2 GiB",
      working: "Working…",
      upload: "Upload",
      used: "{x} used",
      free: "{free} free of {total}",
      loading: "Loading library…",
      empty: "No media yet. Upload an image, GIF, PDF or video to start your library.",
      loadMore: "Load more",
      pages: "{n} pages",
      deleteAria: "Delete asset",
    },
  },
  artist: {
    utility: {
      loading: "Loading parcel utility…",
      referral: {
        title: "Referral",
        yourCode: "Your referral code",
        copyLink: "Copy referral link",
        copied: "Copied",
        qualified: "Qualified referrals",
        pending: "Pending referrals",
        empty: "No referrals yet. Share your link — a referral qualifies once your invitee makes their first primary land acquisition.",
        preparing: "Your referral code is being prepared from canonical ownership — check back shortly.",
        ownToUnlock: "Own at least one parcel to unlock your referral code.",
        wasReferred: "You were referred · status",
      },
      parcel: {
        title: "Parcel Utility",
        empty: "You don't currently own any parcel. Acquire land to unlock parcel utility.",
        parcels: "Parcels",
        totalArea: "Total area",
        clusters: "Clusters",
        largestCluster: "Largest cluster",
        flagBasicIdentity: "Basic identity",
        flagPublishable: "Publishable",
        flagPublished: "Published",
        flagHasNeighbors: "Has neighbors",
        neighbors: "{n} neighbors",
        cluster: "cluster {size} · {area} u²",
      },
      neighborhood: {
        title: "Neighborhood",
        empty: "Own a parcel to see your neighborhood.",
        edgeNeighbors: "Edge neighbors",
        connectedClusters: "Connected clusters",
        connectedClusterLabel: "Connected cluster",
        clusterSummary: "{size} parcels · {area} u²",
        openParcel: "Parcel #{id} — {n} edge neighbor(s)",
        loading: "Loading…",
        noEdgeNeighbors: "No edge neighbors.",
        yours: "yours",
        published: "published",
      },
    },
    profile: {
      loading: "Loading profile…",
      connectWallet: "Connect your wallet",
      connectBody: "Sign in to view your Artist profile, eligibility and Creative Capacity.",
      goToSignIn: "Go to sign in",
      unavailable: "Profile unavailable.",
      retry: "Retry",
      loadFail: "Failed to load profile",
      moderationLabel: "Moderation",
      moderationMsg: "Message from admin",
      moderationAria: "You have a message from an administrator — open My Territories to read it",
      tabs: { overview: "Overview", rewards: "Rewards", activity: "Activity", analytics: "Analytics", grid: "My Grid" },
      roleTitle: "Role",
      roleArtist: "You are an Artist — a permanent identity earned through land eligibility and tenure.",
      roleNeo: "You are a NeoArtist — a permanent identity with accelerated access to Artist creation once eligible. Same creative capabilities as Artist.",
      roleCreator: "You currently own land, so you can use the creative tools. Creator is a current state — it follows your land ownership and is not a permanent badge.",
      roleUser: "You don't currently own land and haven't unlocked a permanent artistic identity. Acquire land to become a Creator, or reach 50% eligibility for the NeoArtist path.",
      statusCurrentState: "· current state",
      eligTitle: "Artist Eligibility",
      neoThreshold: "NeoArtist threshold · 50%",
      artistThreshold: "Artist threshold · 100%",
      eligUnits: "{units} / {total} eligibility units",
      tierSlots: "{slots} slots · {units}u",
      tenureTitle: "Artist Tenure · 100 days",
      tenureBlock: " (block {block})",
      tenurePending: "First land acquisition recorded on-chain{block}. The exact date is being resolved from canonical chain data — tenure will display once available. No date is shown until it can be proven.",
      firstAcq: "First acquisition: {date}",
      daysLine: "{days} / {min} days",
      reqMet: "requirement met",
      daysRemaining: "{n} days remaining",
      ccTitle: "Creative Capacity",
      ccTotal: "Total",
      ccUsed: "Used",
      ccAvailable: "Available",
      ccNote: "Capacity comes from your owned land and stays bound to each parcel history.",
      neoTitle: "NeoArtist Path",
      neoHavePermanent: "You already hold a permanent {status} identity.",
      neoNeedElig: "Reach 50% Artist Eligibility to unlock the accelerated NeoArtist path (currently {pct}%).",
      neoEligibleBody: "You are eligible for the accelerated NeoArtist path (one-time fee, in USDC or USDT). NeoArtist grants no extra Creative Capacity.",
      neoFee: "Fee: {fee} · {currencies}",
      neoFeeNotConfigured: "Fee not configured yet — no price is shown until The Grid sets it.",
      neoPaymentPending: "Payment integration pending",
      collectionTitle: "Collection",
      collCreatedBy: "Created by",
      collOwned: "Owned",
      collCreatedCount: "{n} created",
      collCreatedEmpty: "No frozen artworks yet.",
      collOwnedEmpty: "No artworks currently owned.",
      editTitle: "Edit Profile",
      editDisplayName: "Display name",
      editBio: "Bio",
      editAvatar: "Avatar URL (https/ipfs)",
      editSaving: "Saving…",
      editSave: "Save",
      editSaved: "Saved",
      editSaveFail: "Save failed",
    },
  },
  social: {
    follow: { follow: "Follow", following: "Following", actionFailed: "Action failed" },
    report: {
      moreOptions: "More options",
      reason: "Reason",
      report: "Report",
      blockUser: "Block user",
      reported: "Reported. Thank you.",
      reportFailed: "Report failed",
      blocked: "Blocked.",
      blockFailed: "Block failed",
      reasons: {
        spam: "Spam", abuse: "Abuse", harassment: "Harassment", hate: "Hate",
        nsfw: "NSFW", scam: "Scam", impersonation: "Impersonation", other: "Other",
      },
    },
    bar: {
      save: "Save", saved: "Saved", comments: "Comments",
      likeError: "Could not update like", saveError: "Could not update save",
    },
    comment: {
      post: "Post", postError: "Could not post", removed: "[removed]", you: "you",
      reply: "Reply", hide: "Hide", viewOne: "View {n} reply", viewMany: "View {n} replies",
      delete: "Delete", loadingReplies: "Loading replies…", replyPlaceholder: "Write a reply…",
      heading: "Comments", topPlaceholder: "Share your thoughts on this Territory…",
      signin: "Sign in to join the conversation →", loading: "Loading comments…",
      empty: "No comments yet. Be the first.", loadingMore: "Loading…", loadMore: "Load more",
    },
    creator: {
      signinPre: "Sign in", signinPost: "to view your profile.",
      none: "No creator specified.", loading: "Loading profile…", unavailable: "Profile unavailable.",
      followers: "Followers", following: "Following", likes: "Likes", comments: "Comments",
      territories: "Territories", published: "Published",
      gotoFeed: "Go to your feed →", recentActivity: "Recent activity",
    },
    feed: {
      title: "Social", yourProfile: "Your profile →",
      following: "Following", discover: "Discover",
      followingSignin: "Sign in to see the creators you follow →",
      loading: "Loading feed…", unavailable: "Feed unavailable.",
      emptyFollowing: "Your feed is quiet. Follow creators to see their latest Territories and artworks here.",
      emptyDiscover: "Nothing to discover yet — new published Territories appear here.",
      loadingMore: "Loading…", loadMore: "Load more",
      titlePublication: "Published a Territory",
      titleArtworkNamed: "Published artwork “{title}”",
      titleArtwork: "Published an artwork",
      titleAcquiredParcel: "Acquired parcel #{id}",
      titleAcquired: "Acquired new Territory",
    },
    handle: {
      heading: "Your social profile", handleLabel: "Handle", handlePlaceholder: "your_handle",
      save: "Save", handleSaved: "Handle saved.", handleError: "Could not save handle",
      handleHint: "3–24 chars · letters, digits, underscore · changeable every 30 days.",
      linksLabel: "Links", linkLabelPlaceholder: "Label", addLink: "+ Add link",
      saveLinks: "Save links", linksSaved: "Links saved.", linksError: "Could not save links",
    },
  },
  legal: {
    navAria: "Legal document navigation",
  },
  editor: {
    pixel: {
      title: "Pixel Studio",
      deleteTitle: "Delete this pixel art",
      delete: "Delete",
      closeTitle: "Close the studio",
      startingPoint: "Starting Point",
      startingPointBody: "We turned your image into pixels to give you a base. Now make it yours…",
      pencil: "Pencil", eraser: "Eraser", fill: "Fill", pick: "Pick",
      undo: "Undo", redo: "Redo", clear: "Clear", grid: "Grid",
      importOpen: "Import image → Pixel",
      brush: "Brush", zoom: "Zoom",
      zoomOutTitle: "Zoom − (smaller cells)", zoomInTitle: "Zoom + (larger cells)",
      cellsPainted: "Cells painted:",
      paletteActive: "Active palette", paletteActiveNote: "· imported image / drawing",
      add: "+ Add", recent: "Recent",
      paletteBase: "Base palette", paletteBaseNote: "· standard THE GRID colours",
      saving: "Saving…", saveArtworkCanvas: "Save artwork + canvas",
      unsaved: "Unsaved", saved: "Saved", close: "Close",
      pixelizeTitle: "Pixelize image", cancel: "Cancel",
      resultingGrid: "Resulting grid:", colorsLabel: "colours:",
      resolution: "Resolution (long side)", fitMode: "Fit",
      fitContain: "Contain", fitCover: "Cover", maxColors: "Max colours",
      dithering: "Dithering", on: "ON", off: "OFF",
      alphaThreshold: "Transparency threshold ({v})", useAsBase: "Use as base",
      unsavedTitle: "Unsaved changes",
      unsavedBody: "You have pixel-art changes not yet saved to the canvas. What do you want to do?",
      saveAndClose: "Save and close", discardClose: "Close without saving",
    },
    voxel: {
      invalid: "Invalid 3D editor route.",
      authRequired: "Sign in with your wallet to edit this 3D scene.",
      signIn: "Sign in →",
      loading: "Loading 3D editor…",
      back: "Back",
      empty: "No territory.",
      failedToLoad: "Failed to load",
      noFootprint: "This territory has no owned parcels to build on.",
      flashCreateFirst: "Create or select a Work Art first",
      flashOutside: "Outside your owned footprint",
      flashCleared: "Cleared active piece",
      flashNoImage: "Could not read image",
      flashBaseInside: "Base must stay inside your territory",
      canvas: "Canvas",
      add: "Add", remove: "Remove", cancelBase: "Cancel base",
      currentColorTitle: "Current color (change it in the sidebar)",
      undo: "Undo", undoTitle: "Undo (Ctrl+Z)",
      redo: "Redo", redoTitle: "Redo (Ctrl+Shift+Z)",
      clear: "Clear", clearTitle: "Clear the active piece",
      resetView: "Reset View",
      saving: "Saving…", saved: "Saved", save: "Save",
      saveTitle: "Save 3D draft", saveOverTitle: "Over capacity — remove voxels to save",
      publishTitle: "Publishing happens on the Canvas — it automatically includes this saved 3D scene",
      publishLink: "Publish on Canvas →",
      reload: "Reload",
      color: "Color", customColorTitle: "Custom color",
      workArts: "Work Arts", newBase: "+ New base",
      budgetLabel: "Budget:", cellsUnit: "cells",
      noWorkArts: "No work arts yet.",
      activePiece: "Active piece", rotate: "Rotate", rotationHint: "Reset rotation to 0° to edit voxels.",
      imageToVoxel: "Image → Voxel", depth: "Depth", converting: "Converting…", chooseImage: "Choose image…",
      conversionHelp: "Conversion helps you get started with a simple extrusion. You can refine the result voxel by voxel.",
      fallback: "3D editing needs WebGL, which this device could not start. Your 3D draft is safe — try a desktop browser with hardware acceleration.",
      defineTitle: "Define base — drag on the grid",
      defineBody: "Drag a rectangle on the floor to size your piece, then release to create it.",
      sizeLabel: "Size", tooBig: "· too big",
      noCapacityTitle: "No free 3D capacity",
      noCapacityBody: "You can't place blocks on this territory yet. The free 3D gift (10,000+ voxels) is granted only to the wallet that bought a parcel in the primary sale and still owns it. Parcels that were claimed/minted or bought second-hand receive none.",
      hintNewBase: "Click “+ New base”, then drag on the grid to size your piece.",
      howToBuild: "How to build",
      step1: "Click + New base, then drag on the grid to size the piece",
      step2: "Left-click the floor / a block's face to place blocks",
      step3: "Right-click a block to remove it",
      mouse: "Mouse", showControls: "Show controls & how to build", hide: "Hide",
      leftClick: "Left click", rightClick: "Right click", ctrlLeft: "Ctrl + left", ctrlRight: "Ctrl + right", scroll: "Scroll",
      drawBase: "Draw base", placeBlock: "Place block", removeBlock: "Remove block", orbit: "Orbit around click", pan: "Pan the view", zoomInOut: "Zoom in / out",
      capacity3d: "3D Capacity", overCapacity: "Over capacity",
    },
    templates: {
      title: "Canvas Templates",
      curatedStarts: "6 curated starts",
      close: "Close templates",
      apply: "Apply template",
      previewAria: "{name} layout preview",
      replaceTitle: "Replace current draft?",
      replaceBody: "Applying {name} will replace your current draft content ({count} {unit}). Your published territory is not affected until you Save and Publish. This cannot be undone except with Undo.",
      elementOne: "element",
      elementOther: "elements",
      cancel: "Cancel",
      replaceDraft: "Replace draft",
      catalog: {
        creator: { name: "Creator / Personal Brand", useCase: "Personal identity", tagline: "A bold centred identity — name, role, bio, socials and a single call to action.", tag0: "Identity", tag1: "Bio", tag2: "Socials" },
        retail: { name: "Retail / Storefront", useCase: "Small brand & shop", tagline: "A brand header over a rhythmic product shelf, with a clear visit-shop action.", tag0: "Products", tag1: "Shelf", tag2: "Shop link" },
        gallery: { name: "Artist / Gallery", useCase: "Artwork-first", tagline: "A single dominant frame with a quiet statement — the work leads, words follow.", tag0: "Artwork", tag1: "Statement", tag2: "Minimal" },
        music: { name: "Music / Event", useCase: "Release & live event", tagline: "High-energy title, date and place, and a direct tickets / stream action.", tag0: "Event", tag1: "Date", tag2: "Tickets" },
        product: { name: "Product / Tech Launch", useCase: "Launch landing", tagline: "A modern launch page — headline, supporting line, three feature blocks and a CTA.", tag0: "Launch", tag1: "Features", tag2: "CTA" },
        minimal: { name: "Minimal Luxury", useCase: "High-end minimalism", tagline: "Restrained typography and generous space — one word, one line, one action.", tag0: "Luxury", tag1: "Whitespace", tag2: "Restraint" },
      },
    },
    error: {
      title: "The canvas hit an error",
      body: "The editor recovered instead of going blank. Unsaved changes since your last Save were not written to the server. Try dismissing to continue, or reload the editor for a clean state.",
      dismiss: "Dismiss",
      reload: "Reload editor",
    },
    canvas: {
      invalid: "Invalid canvas route.",
      authRequired: "Sign in with your wallet to edit this Canvas.",
      signIn: "Sign in →",
      loading: "Loading Canvas…",
      empty: "No canvas.",
      failedToLoad: "Failed to load",
      back: "Back",
      close: "Close",
      cancel: "Cancel",
      exitConfirm: "You have unsaved changes. Leave without saving?",
      flash: {
        saved3d: "3D position saved",
        territoryChanged3d: "Territory changed — reload the editor",
        error3d: "3D: {code}",
        rotateOutside: "Rotation would leave the territory",
      },
      save: {
        territoryChanged: "Territory changed — reload editor",
      },
      toolbar: {
        territories: "Territories",
        undo: "Undo",
        redo: "Redo",
        fit: "Fit Territory",
        templates: "Templates",
        templatesTitle: "Start from a premium template",
        templatesDisabledTitle: "Templates need an owned territory",
        mediaLibrary: "Media Library",
        mediaLibraryTitle: "Media Library — upload & browse without leaving the editor",
        artistProfile: "Artist Profile",
        artistProfileTitle: "Open your Artist Profile",
        editor3d: "3D Editor",
        editor3dTitle: "Open the 3D Voxel Editor for this Territory",
        save: "Save",
        saving: "Saving…",
        saveTitle: "Save canvas",
        saveOverTitle: "Over capacity — reduce GP usage to save",
        freeze: "Freeze",
        freezeTitle: "Freeze this Pixel Studio artwork",
        freezeBlockedTitle: "Save the canvas before freezing",
      },
      tools: {
        pixelArt: "Pixel Art",
        new: "NEW",
        text: "Text",
        interactive: "Interactive",
        media: "Media",
        shapes: "Shapes",
      },
      pixelDraw: {
        banner: "Draw a rectangle on your territory",
      },
      pixelSize: {
        title: "New Pixel Art",
        body: "Choose a canvas size — 1 cell = 1 parcel. Empty cells stay transparent.",
        custom: "Custom — draw on the territory",
      },
      mobileReadonly: "Read / inspect only on this device. Editing requires a larger screen.",
      gp: {
        used: "Used",
        available: "Available {n} GP",
        overCapacity: "Over capacity",
      },
      status: {
        saved: "Saved",
        unsavedChanges: "Unsaved changes",
        saving: "Saving…",
        error: "Error",
        showError: "Show full error",
      },
      inspector: {
        territoryTiers: "Territory Tiers",
        globalStyle: "Global style",
        background: "Background",
        bgColor: "BG color",
        perimeter: "Perimeter",
        perimeterGlow: "Perimeter glow",
        opacity: "Opacity {v}%",
        glow: "Glow",
        glowColor: "Glow color",
        text: "Text",
        label: "Label",
        subtitleOptional: "Subtitle (optional)",
        urlHttps: "URL (https)",
        previewOpen: "Preview / Open ↗",
        fill: "Fill",
        lineColor: "Line color",
        fit: "Fit",
        pixelArt: "Pixel Art",
        editPixels: "Edit pixels",
        title: "Title",
        bgPreview: "Background preview",
        changeImage: "Change image",
        addImage: "+ Add image",
        selectedGp: "GP {used} / {max} max",
      },
      controls: {
        preset: "Preset",
        layout: "Layout",
        direction: "Direction",
        columns: "Columns {v}",
        align: "Align",
        gap: "Gap {v}",
        iconSize: "Icon size {v}",
        labelOptional: "Label (optional)",
        labelColor: "Label color",
        brandColors: "Brand colors",
        iconColor: "Icon color",
        backgroundChip: "Background chip",
        bgColor: "BG color",
        border: "Border",
        borderColor: "Border color",
        radius: "Radius",
        padding: "Padding {v}",
        hover: "Hover",
        font: "Font",
        color: "Color",
        textSize: "Text size {v}",
        style: "Style",
        subtitleColor: "Subtitle color",
        underline: "Underline label",
        icon: "Icon",
      },
      social: {
        links: "Links ({n}/5)",
        removeLink: "Remove link",
        addLink: "+ Add link",
        showLabels: "Show labels",
        labelFont: "Label font",
        labelSize: "Label size {v}",
      },
      contacts: {
        heading: "Contacts ({n}/10)",
        removeContact: "Remove contact",
        addContact: "+ Add contact",
        show: "Show",
        value: "Value",
        dataColor: "Data color",
        kind: { EMAIL: "Email", PHONE: "Phone", WHATSAPP: "WhatsApp", WEBSITE: "Website", MAP: "Address" },
        placeholder: { EMAIL: "name@example.com", PHONE: "+1 555 000 1234", WHATSAPP: "+1 555 000 1234", WEBSITE: "https://example.com", MAP: "123 Main St, City" },
      },
      layers: {
        title: "Layers",
        empty: "No modules yet.",
        front: "Front",
        forward: "Forward",
        backward: "Backward",
        back: "Back",
        hide: "Hide",
        lock: "Lock",
      },
      mediaPanel: {
        title: "Media Library",
        working: "Working…",
        upload: "Upload",
        hint: "Image 10 · GIF 20 (10s) · PDF 25 · Video 250 MiB · Library 2 GiB. Uploads appear instantly in the Add-media pickers.",
        loading: "Loading library…",
        empty: "No media yet. Upload an image, GIF, PDF or video to start your library.",
        loadMore: "Load more",
        deleteAria: "Delete asset",
      },
      mediaPicker: {
        choose: "Choose {kind} from Media Library",
        empty: "No {kind} assets. Upload in the Dashboard Media Library first.",
      },
      mediaKind: { image: "image", gif: "GIF", video: "video", pdf: "PDF" },
      errorModal: {
        saveFailed: "Save failed",
        gotIt: "Got it",
      },
      tier: { STANDARD: "Standard", PREMIUM: "Premium", VIP_CORE: "VIP Core", RESERVE: "Reserve", GRID_ZERO: "GRID ZERO" },
      enum: {
        module: { TITLE: "Title", TAGLINE: "Tagline", DESCRIPTION: "Description", LINK: "Link", SOCIAL: "Social", CONTACTS: "Contacts", IMAGE: "Image", GIF: "GIF", VIDEO: "Video", PDF: "PDF", PIXEL_ART: "Pixel Art", SHAPE: "Shape" },
        shape: { RECTANGLE: "Rectangle", ROUNDED_RECTANGLE: "Rounded rectangle", ELLIPSE: "Ellipse", LINE: "Line" },
        bgMode: { NEUTRAL: "Neutral", SOLID_COLOR: "Solid color" },
        glow: { OFF: "Off", LOW: "Low", MEDIUM: "Medium", HIGH: "High" },
        direction: { HORIZONTAL: "Horizontal", VERTICAL: "Vertical", GRID: "Grid" },
        align: { START: "Start", CENTER: "Center", END: "End" },
        textAlign: { LEFT: "Left", CENTER: "Center", RIGHT: "Right", JUSTIFY: "Justify" },
        radius: { SQUARE: "Square", SLIGHT: "Slight", ROUNDED: "Rounded", PILL: "Pill" },
        hover: { NONE: "None", LIFT: "Lift", GLOW: "Glow", SCALE: "Scale" },
        fit: { COVER: "Cover", CONTAIN: "Contain" },
        contactsDisplay: { ICON: "Icon", ICON_LABEL: "Icon + label", ICON_DATA: "Icon + data", BUTTON: "Button" },
        linkIcon: { NONE: "None", ARROW: "Arrow", FAVICON: "Favicon" },
        socialPreset: { ICONS_ONLY: "Icons only", MINIMAL: "Minimal", BUTTONS: "Buttons", CARDS: "Cards" },
        contactsPreset: { ICONS_ONLY: "Icons only", BUSINESS: "Business", MINIMAL: "Minimal" },
        linkPreset: { TEXT: "Text", BUTTON: "Button", OUTLINE: "Outline", CARD: "Card" },
      },
    },
  },
};

export type Dict = typeof en;
export default en;
